import { Mp3Encoder } from "@breezystack/lamejs";

const KBPS = 64;
const SAMPLE_BLOCK_SIZE = 1152;

/**
 * Converts a WebM/Opus audio blob to MP3 using the Web Audio API + lamejs.
 * WhatsApp doesn't support audio/webm, but does support audio/mpeg (MP3).
 */
export const convertWebmToMp3 = async (webmBlob: Blob): Promise<File> => {
  // Use default sample rate (matches system output, typically 48kHz)
  // Don't force 44100 — it can cause decodeAudioData to fail
  const audioContext = new AudioContext();

  try {
    const arrayBuffer = await webmBlob.arrayBuffer();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
    const sampleRate = audioBuffer.sampleRate;

    // Get mono PCM data as Float32Array, then convert to Int16Array for lamejs
    const float32 = audioBuffer.getChannelData(0);
    const int16 = new Int16Array(float32.length);
    for (let i = 0; i < float32.length; i++) {
      const clamped = Math.max(-1, Math.min(1, float32[i]));
      int16[i] = clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff;
    }

    const mp3Encoder = new Mp3Encoder(1, sampleRate, KBPS);
    const mp3Chunks: Uint8Array[] = [];

    for (let i = 0; i < int16.length; i += SAMPLE_BLOCK_SIZE) {
      const chunk = int16.subarray(i, i + SAMPLE_BLOCK_SIZE);
      const encoded = mp3Encoder.encodeBuffer(chunk);
      if (encoded.length > 0) {
        mp3Chunks.push(encoded);
      }
    }

    const finalChunk = mp3Encoder.flush();
    if (finalChunk.length > 0) {
      mp3Chunks.push(finalChunk);
    }

    const mp3Blob = new Blob(mp3Chunks, { type: "audio/mpeg" });
    return new File([mp3Blob], "voice-note.mp3", { type: "audio/mpeg" });
  } finally {
    await audioContext.close();
  }
};
